import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import "echarts-wordcloud";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { useSelector } from "react-redux";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { getSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { formatNumber } from "../Utils/FormatNumbers";
import { sortData } from "../Utils/SortData";
import { CustomLegend } from "../Custom/CustomLegendContent";

const FunnelChartComp = ({
    Data,
    Sheetid,
    valueKeys,
    toolTipKeys,
    rowKeys: rawGroupByKeys,
    groupByKeys: rawRowKeys,
    chartType,
    sortProperties,
}) => {
    // console.log(sortProperties);

    // console.log(
    //     Data,
    //     rawGroupByKeys,
    //     valueKeys,
    //     rawRowKeys,
    //     chartType,
    //     toolTipKeys
    // );

    const customizationObject = useSelector((state) =>
        getL1SheetProperty(state, Sheetid, "customization")
    );
    const isAnimataionOff = useSelector(
        getSheetControls({ Sheetid: Sheetid, key: "isAnimataionOff" })
    );
    const sorteData = sortData({
        transformedData: Data,
        sortProperties,
        requiredArray: rawRowKeys,
    });
    // console.log(sorteData, sortProperties);

    const {
        colorSchemeid,
        transparency = 100,
        isLabelVisible,
        labelFontSize = 14,
        labelPosition = "bottom",
        labelFontColor = "",
        colorMapping,
        isLegendVisible,
        legendAlign,
        legendVerticalAlign,
        legendLayout,
        labelFunnelFontSize,
        labelFunnelFontColor,
        isLabelfunnelVisible,
        labelContents,
        isTileVisible,
        isTitleBold,
        Title,
        TitleFontColor,
        TitleSize,
        valueAlign,
    } = customizationObject;
    // console.log(
    //     isLegendVisible,
    //     legendAlign,
    //     legendVerticalAlign,
    //     legendLayout,
    //     labelFunnelFontSize
    // );
    // console.log(colorMapping);

    const colorScheme = getColourSchemeByID(colorSchemeid);
    const COLORS = colorScheme?.colors || [];
    // console.log(COLORS);

    const rowKey =
        rawRowKeys?.[0]?.responseKey || rawGroupByKeys?.[0]?.responseKey;
    const valueKey = valueKeys?.[0]?.responseKey;
    // console.log(rowKey, valueKey);
    const funnelData = useMemo(() => {
        if (!Data || !rowKey || !valueKey) return [];

        return Data.map((item) => ({
            name: item[rowKey],
            value: Number(item[valueKey]) || 0,
        }));
    }, [Data, rowKey, valueKey]);
    // console.log(funnelData);

    const containerRef = useRef(null);

    const option = {
        tooltip: {
            backgroundColor: "transparent",
            borderWidth: 0,
            padding: 0,
            formatter: function (params) {
                // console.log(params);

                const valueKeyCaption = valueKeys?.[0]?.caption || "Value";
                const numberFormat = valueKeys?.[0]?.numberFormat;

                const formattedValue = formatNumber(params.value, numberFormat);
                // console.log(formattedValue);

                const heading = `<div style="font-weight: 600; font-size: 16px;">${params.name}</div>`;
                const content = `<div style="font-size: 15px; margin-top: 2px;">${valueKeyCaption}: ${formattedValue}</div>`;

                const dataItem = params?.data?.rawData || {};

                const additional = toolTipKeys
                    ?.map(({ responseKey, caption, numberFormat }) => {
                        const label = caption || responseKey;

                        const dataItem = Array.isArray(Data) ? Data[0] : Data;
                        const rawValue = dataItem?.[responseKey];

                        // Convert to number if it's numeric-like
                        const isNumeric =
                            !isNaN(rawValue) && rawValue !== null && rawValue !== "";
                        const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";

                        const formattedValue = isNumeric
                            ? formatNumber(value, numberFormat) // Apply dynamic formatting
                            : value;

                        return `<div style="font-size: 15px; color: #4B5563;">${label}: ${formattedValue}</div>`;
                    })
                    .join("");

                return `
      <div 
        class="border border-gray-200 rounded bg-white p-[10px]"
        style="
          background: rgba(255, 255, 255, 0.9);
          padding: 10px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border-radius: 6px;
        "
      >
        <div style="color: #374151; display: flex; flex-direction: column; gap: 4px;">
          ${heading}
          ${content}
          ${additional}
        </div>
      </div>
    `;
            },
            extraCssText: "pointer-events: auto;",
        },

        animation: !isAnimataionOff,
        legend: {
            show: isLegendVisible,
            orient: legendLayout.value,
            top: legendVerticalAlign.value,
            left: legendAlign.value,
            textStyle: {
                fontSize: 14,
                color: "#333",
            },
            itemGap: 2,

            data: funnelData.map((item) => ({
                name: item.name,
                icon: "rect", // 👈 This sets the shape to a rectangle
            })),
        },

        series: [
            {
                name: "Funnel",
                type: "funnel",
                left: "10%",
                top: 30,
                bottom: 40,
                width: "80%",
                minSize: "0%",
                maxSize: "100%",
                sort: "none",
                gap: 1,
                label: {
                    show: isLabelfunnelVisible,
                    position: "inside",
                    fontSize: labelFunnelFontSize,
                    color: labelFunnelFontColor || "#000",
                    formatter: function (params) {
                        const responseKey = labelContents?.value || "value";
                        const numberFormat = valueKeys?.[0]?.numberFormat;
                        const dataItem = params?.data;

                        const rawValue = dataItem?.[responseKey];
                        const isNumeric = typeof rawValue === "number";
                        const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";

                        const formatted = isNumeric
                            ? formatNumber(value, numberFormat)
                            : value;

                        return formatted;
                    },
                },
                emphasis: {
                    label: {
                        fontSize: labelFunnelFontSize,
                        fontWeight: "bold",
                        color: labelFunnelFontColor,
                        formatter: function (params) {
                            const responseKey = labelContents?.value || "value";
                            const numberFormat = valueKeys?.[0]?.numberFormat;
                            const dataItem = params?.data;

                            const rawValue = dataItem?.[responseKey];
                            const isNumeric = typeof rawValue === "number";
                            const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";

                            const formatted = isNumeric
                                ? formatNumber(value, numberFormat)
                                : value;

                            return formatted;
                        },
                    },
                },
                itemStyle: {
                    color: function (params) {
                        // Assign colors dynamically based on the index
                        return COLORS[params.dataIndex % COLORS.length];
                    },
                },

                data: funnelData,
            },
        ],
    };

    return (
        <div style={{ width: "100%", height: "100%" }}>
            <div style={{ width: "100%", height: isTileVisible ? "5%" : "0%" }}>
                {isTileVisible && (
                    <div
                        className={`w-auto ${isTitleBold ? "font-bold" : ""} ${valueAlign === "left"
                            ? "text-left"
                            : valueAlign === "right"
                                ? "text-right"
                                : "text-center"
                            }`}
                        style={{
                            fontSize: TitleSize,
                            color: TitleFontColor ?? "#bababa",
                        }}
                    >
                        {Title || " Title..."}
                    </div>
                )}
            </div>
            <div style={{ width: "100%", height: isTileVisible ? "95%" : "100%" }}>
                <ReactECharts
                    option={option}
                    style={{ height: "100%", width: "100%" }}
                />
            </div>
        </div>
    );
};

export default FunnelChartComp;
